import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import axios from "axios";
import MagneticButton from "./MagneticButton";
import CountUp from "./CountUp";
import { API } from "../lib/api";

const BANNER_IMG =
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_moebus-karriere/artifacts/0vid8e89_3e285a55-2469-4d70-b71f-cc5e892ca66a.png";
const BACKEND_BASE = API.replace(/\/api$/, "");

function scrollTo(id) {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 72, behavior: "smooth" });
}

export default function Hero() {
    const ref = useRef(null);
    const [banner, setBanner] = useState(BANNER_IMG);

    useEffect(() => {
        axios
            .get(`${API}/settings/public`)
            .then(({ data }) => {
                if (data.hero_image) {
                    setBanner(data.custom ? `${BACKEND_BASE}${data.hero_image}` : data.hero_image);
                }
            })
            .catch(() => {});
    }, []);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);
    const bannerOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);
    const bannerBlur = useTransform(scrollYProgress, [0, 0.45], ["blur(0px)", "blur(10px)"]);
    const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative bg-moebus-deep overflow-hidden"
        >
            {/* Animated dot grid background */}
            <div className="absolute inset-0 dot-grid opacity-40"></div>
            <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-3xl float-soft"></div>
            <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-blue-500/10 blur-3xl float-soft" style={{ animationDelay: "1.5s" }}></div>

            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent z-10"></div>

            <div className="relative pt-24 md:pt-28 pb-12 md:pb-16">
                <div className="container mx-auto px-4 md:px-8">
                    {/* Location badge */}
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="flex justify-center mb-6"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 backdrop-blur border border-white/10 text-white/80 text-xs tracking-widest uppercase">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                            </span>
                            <MapPin size={14} className="text-emerald-400" />
                            Neustadt in Sachsen · Deutschland & Europa
                        </div>
                    </motion.div>

                    {/* Banner showcase with parallax */}
                    <motion.div
                        data-testid="hero-banner"
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        style={{ y, scale, opacity: bannerOpacity, filter: bannerBlur }}
                        className="relative max-w-5xl mx-auto rounded-xl overflow-hidden shadow-[0_30px_80px_-15px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
                    >
                        <img
                            src={banner}
                            alt="Möbus Umweltschutz – Da fehlst du."
                            className="w-full h-auto block"
                            loading="eager"
                        />
                        {/* Glow ring on hover */}
                        <div className="absolute inset-0 ring-1 ring-emerald-400/0 hover:ring-emerald-400/30 transition-all duration-500 rounded-xl pointer-events-none"></div>
                    </motion.div>

                    {/* CTAs */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.45 }}
                        className="mt-20 md:mt-24 flex flex-col sm:flex-row gap-4 justify-center items-center"
                    >
                        <MagneticButton>
                            <button
                                data-testid="hero-apply-btn"
                                onClick={() => scrollTo("bewerbung")}
                                className="group relative inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-600 text-white font-medium px-8 py-4 rounded-md transition-all shadow-lg shadow-emerald-900/40 overflow-hidden"
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    Jetzt bewerben
                                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                </span>
                                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                            </button>
                        </MagneticButton>
                        <MagneticButton>
                            <button
                                data-testid="hero-jobs-btn"
                                onClick={() => scrollTo("stellen")}
                                className="inline-flex items-center justify-center gap-2 bg-white/5 backdrop-blur border border-white/20 hover:bg-white/10 text-white font-medium px-8 py-4 rounded-md transition-all"
                            >
                                Stellen ansehen
                            </button>
                        </MagneticButton>
                    </motion.div>

                    {/* Subline */}
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        style={{ opacity }}
                        data-testid="hero-subline"
                        className="mt-10 text-center text-base md:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed"
                    >
                        Werde Teil unseres Teams am Standort in Neustadt in Sachsen
                        <br />
                        ob im Büro, in der Technik oder auf der Baustelle.
                    </motion.p>

                    {/* Stats row */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-12 md:mt-16 grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto"
                    >
                        {[
                            { vRender: () => (<><CountUp to={50} duration={2} />+</>), l: "Jahre Erfahrung" },
                            { vRender: () => "Tunnel · Bau · Industrie", l: "Projekte" },
                            { vRender: () => "Bundesweit", l: "Im Einsatz" },
                        ].map((s, idx) => (
                            <div
                                key={s.l}
                                className="text-center border-l border-white/15 first:border-l-0 first:pl-0 pl-4 md:pl-6"
                            >
                                <div className="font-heading text-white font-semibold text-base md:text-xl">
                                    {s.vRender()}
                                </div>
                                <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/50 mt-1.5">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* Bottom fade */}
            <div className="h-12 bg-gradient-to-b from-transparent to-white"></div>
        </section>
    );
}
